import MapFeature from './MapFeature'
import { WEB_APP_URLS } from '@bikemap/js/settings'
import { RouteMarker, Cluster } from '../markers'
import MapContent from './MapContent'
import { getWebAppParams } from '@bikemap/js/integrations/global/web-app-links'

const ID = 'routes'
const CLUSTER_MAX_ZOOM = 12
const CLUSTER_RADIUS = 35

const invisibleLayer = {
    type: 'circle',
    paint: {
        'circle-opacity': 0,
    },
}

/**
 * Show zoom and bearing controls on the map.
 */
export default class ClusteredRoutes extends MapFeature {

    /** @type {import("../../entities").Route[]} */
    _routes = []

    /** @type {Object} */
    _routeMarkers = {}

    /** @type {Object} */
    _routeMarkersOnScreen = {}

    /** @type {Object} */
    _routeClusters = {}

    /** @type {Object} */
    _routeClustersOnScreen = {}

    /** @type {MapContent} */
    _mapContentFeature

    constructor() {
        super()

        this._handleDataUpdate = this._handleDataUpdate.bind(this)
        this._updateRouteMarkers = this._updateRouteMarkers.bind(this)

        const source = {
            type: 'geojson',
            data: this._mapRoutesToGeoJSON(),
            cluster: true,
            clusterMaxZoom: CLUSTER_MAX_ZOOM,
            clusterRadius: CLUSTER_RADIUS,
        }
        this._mapContentFeature = new MapContent(ID, source, invisibleLayer)
    }

    /**
     * Show routes indicated by start markers on the map.
     * @param {import("../../entities").Route[]} [routes=[]]
     */
    setRoutes(routes = []) {
        this._routes = routes
        this._mapContentFeature.setData(this._mapRoutesToGeoJSON())
    }

    /**
     * Dimm markers and hide clusters.
     */
    fadeOut() {
        for (const id in this._routeMarkers) {
            this._routeMarkers[id].setOpacity('faded')
        }
        for (const id in this._routeClusters) {
            this._routeClusters[id].setOpacity('transparent')
        }
    }

    /**
     * Fade markers and clusters to full opacity.
     */
    fadeIn() {
        for (const id in this._routeMarkers) {
            this._routeMarkers[id].setOpacity('full')
        }
        for (const id in this._routeClusters) {
            this._routeClusters[id].setOpacity('full')
        }
    }

    _onAdd(map) {
        map.on('data', this._handleDataUpdate)

        this._mapContentFeature.addTo(map)

        map.on('zoomend', this._updateRouteMarkers)
        map.on('moveend', this._updateRouteMarkers)
    }

    _onRemove(map) {
        map.off('data', this._handleDataUpdate)
        map.off('zoomend', this._updateRouteMarkers)
        map.off('moveend', this._updateRouteMarkers)

        this._mapContentFeature.remove()
    }

    _handleDataUpdate(e) {
        if (e.sourceId === ID) {
            // Clear all to minimize the possibility of a broken state
            this._clearClusters()
            this._updateRouteMarkers()
        }
    }

    /**
     * Update markers and clusters that are actually rendered on the map.
     */
    _updateRouteMarkers() {
        const features = this._map.querySourceFeatures(ID)
        const newClusters = {}
        const newMarkers = {}

        // Reuse or create markers and clusters for latest data points
        features.forEach(feature => {
            const { cluster, id, icon } = feature.properties
            if (cluster) {
                const cluster = newClusters[feature.properties.cluster_id] = this._getOrCreateRouteCluster(feature)
                if (!this._routeClustersOnScreen[feature.properties.cluster_id]) cluster.addTo(this._map)
            } else {
                const marker = newMarkers[id] = this._getOrCreateRouteMarker(id, icon, feature.geometry.coordinates)
                if (!this._routeMarkersOnScreen[id]) marker.addTo(this._map)
            }
        })

        // Remove and drop clusters and markers that are not used anymore
        for (const id in this._routeClustersOnScreen) {
            if (!newClusters[id]) this._routeClustersOnScreen[id].remove()
        }
        this._routeClustersOnScreen = newClusters
        for (const id in this._routeMarkersOnScreen) {
            if (!newMarkers[id]) this._routeMarkersOnScreen[id].remove()
        }
        this._routeMarkersOnScreen = newMarkers
    }

    /**
     * Map the currently attached routes to GeoJSON.
     * @returns {Object} GeoJSON data for map source
     */
    _mapRoutesToGeoJSON() {
        return {
            type: 'FeatureCollection',
            features: this._routes.map(route => ({
                type: 'Feature',
                properties: {
                    id: route.id,
                    icon: route.getIcon(),
                },
                geometry: {
                    type: 'point',
                    coordinates: route.start,
                },
            })),
        }
    }

    /**
     * Remove all clusters.
     */
    _clearClusters() {
        for (const id in this._routeClustersOnScreen) {
            this._routeClustersOnScreen[id].remove()
        }
        this._routeClustersOnScreen = {}
        this._routeClusters = {}
    }

    /**
     * Reuse or create a route cluster.
     * @param {Object} feature The cluster feature to represent
     * @returns {Cluster}
     */
    _getOrCreateRouteCluster(feature) {
        const { id, geometry } = feature
        let cluster = this._routeClusters[id]
        if (!cluster) {
            cluster = this._routeClusters[id] = new Cluster(
                geometry.coordinates,
                feature.properties.point_count,
                () => this._zoomToCluster(id, geometry.coordinates)
            )
        }
        return cluster
    }

    /**
     * Zoom to the location of a cluster.
     * @param {*} id ID of the cluster
     * @param {*} coordinates Coordinates of the cluster
     */
    _zoomToCluster(id, coordinates) {
        this._map.getSource(ID).getClusterExpansionZoom(
            id,
            (err, zoom) => {
                if (err) return
                this._map.easeTo({
                    center: coordinates,
                    zoom: zoom,
                })
            }
        )
    }

    /**
     * @param {number} id Route ID
     * @param {string} icon Route icon
     * @param {Object} coordinates Route start point coordinates
     * @returns {RouteMarker}
     */
    _getOrCreateRouteMarker(id, icon, coordinates) {
        let marker = this._routeMarkers[id]
        if (!marker) {
            const url = WEB_APP_URLS.WEB_APP_ROUTE_DETAILS_URL.replace(':id', id) + getWebAppParams()
            marker = this._routeMarkers[id] = new RouteMarker(coordinates, icon, url)

            marker.addEventListener('mouseenter', e => {
                e.routeId = id
                this._dispatchEvent('mouseenterroute', e)
            })
            marker.addEventListener('mouseleave', e => {
                e.routeId = id
                this._dispatchEvent('mouseleaveroute', e)
            })
            marker.addEventListener('click', e => {
                e.routeId = id
                this._dispatchEvent('clickroute', e)
            })
        }
        return marker
    }

}
